import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Container,
	FormControlLabel,
	IconButton,
	Link,
	Paper,
	Snackbar,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import login_cover from '../../../assets/images/login_cover.png';
import logo from '../../../assets/images/logo/logo.png';
import { useAuthentication } from '../../../context/AuthContext';
import { PAGES_URLS } from '../../../lib/constants/routes';
import MuiDialog from '../../ui-component/mui-dialog/MuiDialog';

const BackgroundWrapper = styled('div')({
	backgroundImage: `url(${login_cover})`,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	minHeight: '100vh',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '2rem',
	position: 'relative',
	overflow: 'hidden',

	// Pseudo-element for gradient overlay
	'&::before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: 'linear-gradient(to bottom, #000000, #00000000)',
		zIndex: 1, // Ensure it's between the background and content
	},
	'& > *': {
		position: 'relative',
		zIndex: 2,
	},
});

const LogoSection = styled('div')({
	position: 'absolute',
	top: '36px',
	left: '24px',
	display: 'flex',
	alignItems: 'center',
});

const ContentWrapper = styled(Container)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'start',
	justifyContent: 'space-between',
	color: '#fff',
	maxWidth: '100%',
});

const TextSection = styled(Box)({
	maxWidth: '60%',
	textAlign: 'left',
	marginTop: '100px',
	marginRight: '100px',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
	width: '450px',
	height: '547.86px',
	padding: '4rem 4rem 7rem',
	margin: 'auto',
	[theme.breakpoints.down('sm')]: {
		padding: '4rem 2rem 7rem',
	},
	borderRadius: '0.5rem',
	background: '#FFFFFF80',
	backdropFilter: 'blur(35px)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
	'& .MuiInputBase-root': {
		backgroundColor: 'transparent',
	},
	'& .MuiInputBase-input': {
		'&:-webkit-autofill': {
			WebkitBoxShadow: '0 0 0 1000px transparent inset',
			WebkitTextFillColor: theme.palette.text.primary,
			caretColor: theme.palette.text.primary,
		},
		'&:-webkit-autofill:focus': {
			WebkitBoxShadow: '0 0 0 1000px transparent inset',
			WebkitTextFillColor: theme.palette.text.primary,
			caretColor: theme.palette.text.primary,
		},
		'&:-webkit-autofill:hover': {
			WebkitBoxShadow: '0 0 0 1000px transparent inset',
			WebkitTextFillColor: theme.palette.text.primary,
			caretColor: theme.palette.text.primary,
		},
		'&:-internal-autofill-selected': {
			backgroundColor: 'transparent',
			backgroundImage: 'none',
			color: theme.palette.text.primary,
		},
	},
}));

function Login() {
	const navigate = useNavigate();
	const { login } = useAuthentication();

	const [identifier, setIdentifier] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [rememberMe, setRememberMe] = useState<boolean>(false);
	const [errMsg, setErrMsg] = useState('');
	const [openRegistrationDialog, setOpenRegistrationDialog] = useState(false);
	const [openForgotPassDialog, setOpenForgotPassDialog] = useState(false);

	const handleLogin = async (event: React.FormEvent) => {
		event.preventDefault();
		if (identifier.trim() && password) {
			let res = await login(identifier.trim(), password);
			if (!get(res, 'status.OK')) {
				setErrMsg(get(res, 'errorMsg'));
			}
		} else {
			setErrMsg('E-Mail-Adresse oder Passwort darf nicht leer sein.');
		}
	};
	const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => {
		if (event.key === 'Enter') {
			handleLogin(event);
		}
	};

	return (
		<>
			<BackgroundWrapper>
				<LogoSection>
					<img src={logo} alt='logo' style={{ height: '2.5rem' }} />
				</LogoSection>

				{errMsg && (
					<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={Boolean(errMsg)} autoHideDuration={2000}>
						<Alert severity='error' onClose={() => setErrMsg('')}>
							{errMsg}
						</Alert>
					</Snackbar>
				)}

				<MuiDialog
					open={openRegistrationDialog}
					title='Neue Organisation registrieren'
					content='Hinweis: Sie sind dabei, eine neue Organisation zu registrieren. Wenn Ihre Organisation bereits registriert wurde, wenden Sie sich stattdessen an den Nutzerverwalter Ihrer Organisation. Dieser kann Sie in der Nutzerverwaltung zu dieser Organisation hinzufügen.'
					onClose={() => setOpenRegistrationDialog(false)}
					onConfirm={() => navigate(PAGES_URLS.Registrierung, { state: { registrierenAls: 'KMU' } })}
					onConfirmButtonName='Neue Organisation registrieren'
				/>

				<MuiDialog
					open={openForgotPassDialog}
					title='Passwort vergessen?'
					content='Hinweis: Sie erhalten einen Link zum Zurücksetzen Ihres Passworts an Ihre registrierte E-Mail-Adresse.'
					onClose={() => setOpenForgotPassDialog(false)}
					onConfirm={() => navigate(PAGES_URLS.PasswortVergessen)}
					onConfirmButtonName='Weiter'
				/>
				<ContentWrapper>
					<TextSection>
						<Typography variant='h1' component='h1'>
							Herzlich willkommen beim Antragsportal "Musische Bildung für alle" des VdMK
						</Typography>
						<br />
						<Typography variant='h6'>
							Hier können Musik- und Kunstschulen Förderanträge für die bestehenden Kooperationen (Klasse:Musik, Klasse:Kunst, Inklusive:Musik, Klingende Kita, Kultur:Kita) mit allgemeinbildenden Schulen und Kitas stellen.
						</Typography>
					</TextSection>

					<StyledPaper elevation={3}>
						<Typography variant='h6' sx={{ mb: '1rem', color: '#333' }}>
							Loggen Sie sich ein oder registrieren Sie sich, um Ihre Förderungen zu verwalten.
						</Typography>
						<form onSubmit={handleLogin} onKeyDown={handleKeyPress}>
							<Stack spacing={3}>
								<StyledTextField
									fullWidth
									// label="Benutzername"
									variant='outlined'
									size='medium'
									value={identifier}
									onChange={(e) => setIdentifier(e.target.value)}
								/>

								<StyledTextField
									fullWidth
									// label="Passwort"
									variant='outlined'
									size='medium'
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									InputProps={{
										endAdornment: (
											<IconButton aria-label='toggle password visibility' onClick={() => setShowPassword(!showPassword)}>
												{showPassword ?
													<Visibility />
												:	<VisibilityOff />}
											</IconButton>
										),
									}}
								/>

								<FormControlLabel
									control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} color='primary' />}
									label='Angemeldet bleiben'
								/>

								<Button
									variant='contained'
									color='primary'
									size='large'
									type='submit'
									sx={{
										width: '380px',
										height: '50px',
										borderRadius: '8px',
										padding: '11px 22px',
									}}
								>
									Anmelden
								</Button>

								<Stack direction='row' alignItems={'flex-end'}>
									<Link onClick={() => setOpenForgotPassDialog(true)} sx={{ cursor: 'pointer' }}>
										Passwort vergessen?
									</Link>
								</Stack>
							</Stack>
						</form>
					</StyledPaper>
				</ContentWrapper>
			</BackgroundWrapper>
		</>
	);
}

export default Login;
